import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ComponentWrapper } from '../../helperComponents/componentWrapper/ComponentWrapper';
import { trpc } from '../../../apiCalls/trpcClient';
import { ShadowView } from '../../helperComponents/ShadowView';
import { Image, Platform, StyleSheet, Text, View } from 'react-native';
import { getTopInset } from '../../../utils/shared';
import {
    mbApplyTransparency,
    mbShadow,
    mbTextStyles,
} from '@mightybyte/rnw.utils.style-utils';
import { FONT_STYLES, textStyles } from '../../../constants/textStyles';
import { Spacer } from '../../helperComponents/Spacer';
import { imageUtils } from '../../../utils/imageUtils';
import CountDown from '../../helperComponents/CountDown';
import { GradientButton } from '../../helperComponents/gradients/GradientButton';
import { useNavigation } from '@react-navigation/core';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../../navigations/RootNavigator';
import { QuarterDataToPlay, UserVisit } from '@sapphicsavvy/business';
import Fontisto from 'react-native-vector-icons/Fontisto';
import Card from './Results/components/Card';
import HealthyHeartIcon from '../../../resources/svgComponents/HealthyHeartIcon';
import GameControlIcon from '../../../resources/svgComponents/GameControlIcon';
import { useTracker } from '../../../hooks/useTracker';
// import { envs } from '../../../../env';

const Home = () => {
    const [quarterEnded, setQuarterEnded] = useState(false);
    const navigation =
        useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const trpcUtils = trpc.useUtils();
    const { data: unlockedObj, isLoading: appUnlockedLoading } =
        trpc.quarter.appUnlocked.useQuery();

    const { withTrack } = useTracker();

    const { haveToPlay, haveToViewObj, isUnlocked } = useMemo(
        () => ({
            haveToPlay: unlockedObj?.haveToPlay,
            haveToViewObj: unlockedObj?.haveToView,
            isUnlocked: unlockedObj?.isUnlocked,
        }),
        [unlockedObj],
    );

    const { data: quarterData, isLoading: isQuarterLoading } =
        trpc.quarter.getQuarterDataToPlay.useQuery(
            {
                quarterId: haveToPlay?.quarterId || '',
            },
            { enabled: haveToPlay !== undefined },
        );

    const isLocked = isUnlocked !== undefined ? !isUnlocked : false;

    const { mutate: countView } = trpc.quarter.countView.useMutation();

    const onViewResult = useCallback(() => {
        if (!haveToViewObj) return;

        countView({ resultId: haveToViewObj.resultsId });
        navigation.navigate('QuarterResults', {
            quarterId: haveToViewObj.quarterId,
        });
    }, [countView, navigation, haveToViewObj]);

    useEffect(() => {
        if (
            (haveToPlay?.isPlayed && !quarterData) ||
            haveToPlay?.quarterId !== quarterData?.id
        ) {
            trpcUtils.quarter.getQuarterDataToPlay.invalidate();
        }
    }, [haveToPlay]);

    useEffect(() => {
        if (isUnlocked || haveToViewObj?.isViewed !== haveToPlay?.isPlayed) {
            const intervalId = setInterval(() => {
                trpcUtils.quarter.appUnlocked.invalidate();
            }, 60000);

            return () => clearInterval(intervalId);
        }
    }, [isUnlocked]);

    const onButtonPress = useCallback(() => {
        if (quarterData && !haveToPlay?.isPlayed && haveToPlay?.questionId) {
            const questionIndex = quarterData.questions.findIndex(
                (question) => question.id === haveToPlay.questionId,
            );

            // Guard against questionIndex being -1
            if (
                questionIndex >= 0 &&
                questionIndex < quarterData.questions.length
            ) {
                const targetQuestion = quarterData.questions[questionIndex];

                navigation.navigate('Quarter', {
                    quarterData: quarterData as QuarterDataToPlay,
                    questionIndex,
                    step: 'Guess',
                    answer: {
                        questionId: targetQuestion.id,
                        answerType: targetQuestion.answerType,
                    },
                    answers: [],
                });
            } else {
                navigation.navigate('Stats');
            }
        } else {
            navigation.navigate('Stats');
        }
    }, [haveToPlay, navigation, quarterData]);

    return (
        <ComponentWrapper containerStyle={[styles.container]}>
            <ShadowView style={styles.quarterCard}>
                <ShadowView style={styles.quarterCardInner}>
                    <Image
                        style={styles.logo}
                        source={imageUtils.screens.logoHomeScreen}
                        resizeMode="cover"
                    />
                    <Text style={styles.quarterCardTitleText}>
                        QUARTER {quarterData?.quarterNumber}
                    </Text>
                    <Text style={styles.quarterCardText}>
                        {quarterEnded
                            ? 'QUARTER ENDED'
                            : !quarterData
                            ? 'CLOSED'
                            : 'CLOSES IN'}
                    </Text>
                    <Spacer height={10} />
                    <View>
                        <View style={styles.colors}>
                            <Spacer height={20} color="#0495CD" />
                            <Spacer height={20} color="#6A2C8C" />
                            <Spacer height={20} color="#EE3823" />
                            <Spacer height={20} color="#FCAF41" />
                        </View>
                        {quarterEnded || !quarterData ? (
                            <View style={styles.loading} />
                        ) : appUnlockedLoading || isQuarterLoading ? (
                            <View style={styles.loading}>
                                <Text style={styles.loadingText}>Loading</Text>
                            </View>
                        ) : (
                            <CountDown
                                labelsStyle={styles.countdownLabels}
                                style={styles.countdown}
                                endTime={quarterData?.endTimestamp ?? 0}
                                setQuarterEnded={setQuarterEnded}
                            />
                        )}
                    </View>
                </ShadowView>
            </ShadowView>

            {haveToViewObj && !haveToViewObj.isViewed ? (
                <View>
                    <Text style={styles.viewResultsText}>
                        Check previous results before playing new quarter.
                    </Text>
                    <GradientButton
                        title="VIEW RESULTS"
                        containerStyle={styles.resultBtn}
                        disabled={appUnlockedLoading}
                        onPress={onViewResult}
                    />
                </View>
            ) : (
                <GradientButton
                    title="START"
                    containerStyle={[styles.startBtn]}
                    disabled={
                        appUnlockedLoading ||
                        isQuarterLoading ||
                        haveToPlay?.isPlayed
                    }
                    onPress={onButtonPress}
                />
            )}
            <View>
                <View style={styles.separatorTopLine} />
                <View style={styles.separatorBottomLine} />
            </View>
            <View style={styles.bottomSection}>
                <View style={styles.bottomSectionHeader}>
                    <Text style={styles.bottomSectionTitleText}>
                        Discover More
                    </Text>
                    {isLocked && (
                        <View style={styles.bottomSectionNote}>
                            <Fontisto
                                name="locked"
                                color={mbApplyTransparency('#292E30', 0.55)}
                                size={14}
                            />
                            <Text style={styles.bottomSectionNoteText}>
                                Play Quarter to unlock
                            </Text>
                        </View>
                    )}
                </View>
                <Card
                    icon={<HealthyHeartIcon />}
                    color={['#EE3823', '#FCAF41']}
                    title="Shape"
                    text="Submit & vote on future questions"
                    isLocked={isLocked}
                    onPress={withTrack(UserVisit.shape, () =>
                        navigation.navigate('Shape'),
                    )}
                />
                <Spacer height={15} />
                <Card
                    icon={<GameControlIcon color={'#fff'}/>}
                    color={['#EE3823', '#FCAF41']}
                    title="Mini-Games"
                    text="Play to earn bonus points this Set"
                    isLocked={isLocked}
                    onPress={withTrack(UserVisit.minigames, () =>
                        navigation.navigate('MiniGames'),
                    )}
                />
            </View>
        </ComponentWrapper>
    );
};

export { Home };

const styles = StyleSheet.create({
    container: {
        paddingTop: getTopInset() + 60,
    },
    logo: {
        position: 'absolute',
        width: 85,
        height: 97,
        alignSelf: 'center',
        top: -60,
    },
    quarterCard: {
        padding: 15,
    },
    quarterCardInner: {
        paddingTop: 40,
        paddingBottom: 25,
    },
    quarterCardTitleText: mbTextStyles([
        {
            fontFamily: FONT_STYLES.Tabasco_400,
            textAlign: 'center',
            fontSize: 38,
            color: '#000000',
        },
    ]),
    quarterCardText: mbTextStyles([
        {
            fontFamily: FONT_STYLES.Staatliches_400,
            textAlign: 'center',
            fontSize: 18,
            color: '#EE3823',
            marginBottom: 5,
        },
    ]),
    colors: {
        position: 'absolute',
        top: -10,
        bottom: 0,
        left: 0,
        right: 0,
    },
    countdown: {
        paddingHorizontal: 30,
        maxWidth: 340,
        alignSelf: 'center',
    },
    loading: {
        height: 92,
        alignItems: 'center',
        justifyContent: 'center',
    },
    loadingText: mbTextStyles([
        textStyles.normalText,
        {
            fontFamily: FONT_STYLES.Tabasco_400,
            fontSize: 20,
            fontWeight: '400',
            marginBottom: 30,
        },
    ]),
    countdownLabels: {
        marginTop: 10,
    },
    startBtn: {
        width: 227,
        alignSelf: 'center',
        marginVertical: 20,
    },

    resultBtn: {
        width: 227,
        alignSelf: 'center',
        marginBottom: 20,
    },

    separatorTopLine: {
        height: 1,
        backgroundColor: mbApplyTransparency('#FFFFFF', 0.5),
        marginHorizontal: -20,
        ...mbShadow({
            offsetHeight: -1,
            offsetWidth: 0,
            color: '#FFFFFF',
            opacity: 0.25,
            elevation: 1,
            radius: 10,
        }),
    },
    separatorBottomLine: {
        height: 1,
        backgroundColor: mbApplyTransparency(
            '#8E9396',
            Platform.OS === 'ios' ? 1 : 0.5,
        ),
        marginHorizontal: -20,
        ...mbShadow({
            offsetHeight: 1,
            offsetWidth: 0,
            color: '#000000',
            opacity: 0.05,
            elevation: 1,
            radius: 10,
        }),
    },

    bottomSection: {
        marginTop: 20,
    },
    bottomSectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 15,
    },
    bottomSectionTitleText: mbTextStyles([
        textStyles.normalText,
        {
            fontWeight: '600',
            color: '#292E30',
            textAlign: 'left',
        },
    ]),
    bottomSectionNote: {
        flexDirection: 'row',
        marginLeft: 'auto',
        alignItems: 'center',
    },
    bottomSectionNoteText: mbTextStyles([
        textStyles.smallText,
        {
            color: mbApplyTransparency('#292E30', 0.55),
            marginLeft: 5,
        },
    ]),

    viewResultsText: mbTextStyles([
        textStyles.smallText,
        {
            color: mbApplyTransparency('#292E30', 0.55),
            marginVertical: 10,
        },
    ]),
});
